// The dialog of a document of an estimate: the same for every estimate type, the API says what is offered

export type DocumentFormat = 'docx' | 'pdf';

export type NoticeSeverity = 'error' | 'confirmation' | 'warning' | 'information';

export interface DocumentOption {
    id: string;
    title: string;
    description: string | null;
    // The first one is the default
    formats: DocumentFormat[];
    isOwn: boolean;
}

export interface DocumentNotice {
    severity: NoticeSeverity;
    title: string;
    message: string;
    // The templates the notice concerns, null = all of them
    templateIds: string[] | null;
    imageUrl: string | null;
}

export interface DocumentDownloadOptions {
    templates: DocumentOption[];
    notices: DocumentNotice[];
    isLicensed: boolean;
}

export interface DocumentChoice {
    templateId: string | null;
    format: DocumentFormat;
}

interface StoredChoice {
    templateId: string;
    format: DocumentFormat;
    // Position among the composed templates, null = a template of another kind
    composedPosition: number | null;
}

export interface DownloadedDocument {
    blob: Blob;
    fileName: string;
    // What the document is missing, in words of the user
    missingParts: string[];
}

const LAST_CHOICE_KEY = 'estimate-document-dialog.choice';
const FILE_NAME = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i;

export async function loadOptions(url: string, signal: AbortSignal): Promise<DocumentDownloadOptions> {
    const response = await fetch(url, {credentials: 'same-origin', signal});

    if (!response.ok) {
        throw new Error(`Options of the document failed with ${response.status}`);
    }

    return await response.json() as DocumentDownloadOptions;
}

export async function downloadDocument(
    url: string,
    templateId: string,
    format: DocumentFormat,
    signal: AbortSignal,
): Promise<DownloadedDocument> {
    const query = new URLSearchParams({templateId, format});
    const response = await fetch(`${url}?${query.toString()}`, {method: 'POST', credentials: 'same-origin', signal});

    if (!response.ok) {
        throw new Error(`The document failed with ${response.status}`);
    }

    return {
        blob: await response.blob(),
        fileName: response.headers.get('Content-Disposition')?.match(FILE_NAME)?.[1] ?? `odhad.${format}`,
        missingParts: decodeMissingParts(response.headers.get('X-Document-Missing-Parts')),
    };
}

// The header is base64 of a JSON list, see WarningsHeader in the API
export function decodeMissingParts(header: string | null): string[] {
    if (header === null || header === '') {
        return [];
    }

    try {
        const decoded: unknown = JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(header), character => character.charCodeAt(0))));

        return Array.isArray(decoded) ? decoded as string[] : [];
    } catch {
        return [];
    }
}

export function noticesOf(notices: DocumentNotice[], templateId: string | null): DocumentNotice[] {
    return notices.filter(notice => notice.templateIds === null || (templateId !== null && notice.templateIds.includes(templateId)));
}

export function canDownload(notices: DocumentNotice[], confirmed: DocumentNotice[]): boolean {
    return notices.every(notice => notice.severity !== 'error'
        && (notice.severity !== 'confirmation' || confirmed.includes(notice)));
}

export function formatSize(bytes: number): string {
    return `${(bytes / 1000000).toLocaleString('cs-CZ', {minimumFractionDigits: 1, maximumFractionDigits: 1})} MB`;
}

function isComposed(template: DocumentOption): boolean {
    return template.formats.includes('docx');
}

function lastChoice(): StoredChoice | null {
    try {
        const stored = localStorage.getItem(LAST_CHOICE_KEY);

        return stored === null ? null : JSON.parse(stored) as StoredChoice;
    } catch {
        return null;
    }
}

// The last template when it is still offered. A composed template belongs to one estate type, so another type gets
// the template at the same position: they are ordered from the longest one, a wrong guess is only a preselection.
export function preferredChoice(templates: DocumentOption[]): DocumentChoice {
    const last = lastChoice();
    const template = templates.find(option => option.id === last?.templateId)
        ?? templates.filter(isComposed)[last?.composedPosition ?? -1]
        ?? templates[0];

    return {templateId: template?.id ?? null, format: last?.format ?? template?.formats[0] ?? 'docx'};
}

export function rememberChoice(templates: DocumentOption[], templateId: string, format: DocumentFormat): void {
    const position = templates.filter(isComposed).findIndex(template => template.id === templateId);
    const stored: StoredChoice = {templateId, format, composedPosition: position === -1 ? null : position};

    try {
        localStorage.setItem(LAST_CHOICE_KEY, JSON.stringify(stored));
    } catch {
        // Only a convenience, the download goes on without it
    }
}
